import { Injectable, isDevMode } from "@angular/core";
import { BaseHttpService } from "@app/core/http";
import { HttpClient } from "@angular/common/http";
import { JwtService } from "@app/core/auth";
import { LoaderService } from "./loader.service";
import { BehaviorSubject, map, finalize, Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { Customer } from "../models/customer.model";
import { RoleModel } from "../models/role.model";
import { PlatformModel } from "../models/platform.model";

@Injectable({
    providedIn: 'root'
  })
  export class CustomerService extends BaseHttpService {

    private customersSubject = new BehaviorSubject<Customer[]>([]);
    public customer$ = this.customersSubject.asObservable();

    private customersConnectedSubject = new BehaviorSubject<Customer | undefined>( undefined);
    public customersConnectedSubject$ = this.customersConnectedSubject.asObservable();


    private platformSubject = new BehaviorSubject<PlatformModel | undefined>(undefined);
    public platformSubject$ = this.platformSubject.asObservable();


    private roleSubject = new BehaviorSubject<RoleModel | undefined>(undefined);
    public role$ = this.roleSubject.asObservable();

    // Cache for customers
    private customersCache: Customer[] | null = null;
    private customersLoading: boolean = false;

    private readonly baseApiSpeedPresta = environment.baseApiSpeedpresta;
    private readonly customerUrl = environment.customerUrl;
    private readonly customerRoleUrl = environment.customerRoleUrl;
    private readonly customerWariflowUrl = environment.customerWariflowUrl;

    constructor(
        http: HttpClient,
        private loaderService: LoaderService,
        jwtService: JwtService
      ) {
        super(jwtService,http);
        if (jwtService.decodeToken()) {
            this.getAllCustomer();
            this.getRoleBYUser(jwtService.decodeToken()!.sub);
        }
      }


    getAllCustomer(forceRefresh: boolean = false): void {
        try {
            // Return cached data if available and not forcing refresh
            if (!forceRefresh && this.customersCache) {
                this.customersSubject.next(this.customersCache);
                return;
            }

            // Check if already loading to prevent duplicate requests
            if (this.customersLoading) {
                return;
            }

            const org = this.getUserByToken()?.organization;
            if (!org) return;
            const enterpriseId = org.toLocaleUpperCase();

            // Mark as loading
            this.customersLoading = true;
            this.loaderService.startLoading();

            const userBody = {"status":"active","enterpriseId":enterpriseId,"options":{"options.appId":"ORGANIZATION_CUSTOMERS"},"excluded":{}}
            this.retreiveAllFromKey(`${this.baseApiSpeedPresta}${enterpriseId}/${this.customerUrl}`, userBody)
            .pipe(

                map(customers => customers.map(customers => ({
                    identifier: customers["options"]["identifier"],
                    photo: customers["options"]["photo"],
                    firstname: customers["options"]["firstname"],
                    surname: customers["options"]["surname"],
                    email: customers["options"]["email"],
                    phoneNumber: customers["options"]["mobile"],
                    siret: customers["options"]["siret"],
                    name: customers["options"]["name"],
                    address:  customers["options"]["address"],
                }))),
                finalize(() => {
                    this.loaderService.stopLoading();
                    this.customersLoading = false;
                })
            )
            .subscribe({
                next: (customers) => {
                    this.customersCache = customers as Customer[];
                    this.customersSubject.next(this.customersCache);
                },
                error: (error) => {
                    if (isDevMode()) console.error('[CustomerService] Error loading customers:', error);
                    this.customersLoading = false;
                }
            });
        } catch (error) {
            console.error('[CustomerService] Error loading customers:', error);
            this.loaderService.stopLoading();
            this.customersLoading = false;
        }

    }

    getCustomers(): Observable<Customer[]>{
        return this.customer$;
    }


    getRoleBYUser(email: string): void {
        const org = this.getUserByToken()?.organization;
        if (!org) return;
        const enterpriseId = org.toLocaleUpperCase();

        this.loaderService.startLoading();
        const roleBody = {"enterpriseId":enterpriseId,"options":{"options.account": email},"excluded":{"learning":""}}
        this.retreiveAllFromKey(this.baseApiSpeedPresta + enterpriseId + '/' + this.customerRoleUrl, roleBody)
        .pipe(
            map(role => role.map((role: any ) => ({
                id: role["options"]["id"],
                label: role["options"]["label"],
                account: role["options"]["account"],
            }))),
            finalize(() => this.loaderService.stopLoading())
        )
        .subscribe({
            next: (role: RoleModel[]) => {
                if (role && role.length > 0) {
                    this.roleSubject.next(role[0]);
                }
            },
            error: (error) => {
                if (isDevMode()) console.error('[CustomerService] Error loading role:', error);
            }
        })

    }

    getActualRole(): Observable<RoleModel | undefined> {
        return this.role$;
    }

    getCustomerConnected(email: string): void {
        const org = this.getUserByToken()?.organization;
        if (!org) return;
        const enterpriseId = org.toLocaleUpperCase();

        this.loaderService.startLoading();
        const userBody = {"status":"active","enterpriseId":enterpriseId,"options":{"options.email":email},"excluded":{"learning":""}}
        this.retreiveFromKey(this.baseApiSpeedPresta + enterpriseId + '/' + this.customerWariflowUrl, userBody)
        .pipe(
            map(customers => ({
                identifier: customers["options"]["identifier"],
                photo: customers["options"]["photo"],
                firstname: customers["options"]["firstname"],
                surname: customers["options"]["surname"],
                email: customers["options"]["email"],
                phoneNumber: customers["options"]["mobile"],
            })),
            finalize(() => this.loaderService.stopLoading())
        )
        .subscribe(customer => this.customersConnectedSubject.next(customer as Customer));
    }

    /**
     * Clear customers cache
     * Useful when customers are updated and need to be refreshed
     */
    clearCustomersCache(): void {
        this.customersCache = null;
        this.customersLoading = false;
    }
}